import type { LatLng } from '../maps/types';
import { minutesToTimeString } from '../utils/scheduleConflict';
import { estimateStaticTravelMinutes } from './travelTime';
import type { ScheduleLegInput } from './types';

export type OccupancyLegFields = Pick<
  ScheduleLegInput,
  'legType' | 'earliestStartMinutes' | 'serviceDurationMinutes' | 'endTimeMinutes'
>;

/**
 * Resolve the vehicle-occupancy end (minutes from midnight) for a schedule leg.
 *
 * - standby_block: end_time is the block end (duration may be 0).
 * - transfer / other: service_duration is the source of truth. A placeholder
 *   end_time (e.g. client portal pickup+2h) must not inflate occupancy when
 *   duration is set — that mismatch caused false Vehicle schedule conflicts
 *   on publish.
 */
export function jobLegOccupancyEndMinutes(leg: OccupancyLegFields): number {
  if (leg.legType === 'standby_block') {
    if (leg.endTimeMinutes != null) return leg.endTimeMinutes;
    return leg.earliestStartMinutes + Math.max(leg.serviceDurationMinutes, 0);
  }

  if (leg.serviceDurationMinutes > 0) {
    return leg.earliestStartMinutes + leg.serviceDurationMinutes;
  }

  if (leg.endTimeMinutes != null && leg.endTimeMinutes > leg.earliestStartMinutes) {
    return leg.endTimeMinutes;
  }

  return leg.earliestStartMinutes;
}

export interface ClientTransferLegTimes {
  earliest_start: string;
  latest_start: string;
  end_time: null;
  service_duration_minutes: number;
}

/**
 * Build job_legs time fields for a client transfer request.
 * Matches HQ transfer pattern: duration from travel estimate, end_time null.
 */
export function buildClientTransferLegTimes(params: {
  pickupTime: string;
  pickup: LatLng;
  dropoff: LatLng;
}): ClientTransferLegTimes {
  const service_duration_minutes = estimateStaticTravelMinutes(
    params.pickup,
    params.dropoff
  );
  return {
    earliest_start: params.pickupTime,
    latest_start: params.pickupTime,
    end_time: null,
    service_duration_minutes: Math.max(1, service_duration_minutes),
  };
}

export function occupancyEndTimeString(leg: OccupancyLegFields): string {
  return minutesToTimeString(jobLegOccupancyEndMinutes(leg));
}
